import {RenderConfiguration, RenderResult} from "../renderer/types";
import {WrappedResponse} from "@app/core-app/primary/wrapper";
import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";
import {NotionPageShortData, PageData} from "@app/core-app/app/usecase/notion/fetch-pages";
import {InboxDbConfig, OutboxDbConfig, Profile} from "@app/core-app/app/data/profile";
import Cookies from 'js-cookie'
import {CreateNotionDatabaseInput} from "@app/core-app/app/data/input/create-notion-database.input";
import {createPubEnv, publicEnv} from "@app/core-app/pub-env";
import {CreateNotionPageInput} from "@app/core-app/app/usecase/notion/create-notion-page";
import {RenderRequest} from "@app/core-app/app/usecase/render-best-email";



export type ApiResponse<T> = {
    data: T | null
    code: number
    error: any | null
}


export type EmailContent = {
    body: string
}

export const fetchEmailContent = async (configuration: RenderRequest): Promise<RenderResult | null | undefined> => {
  const nextToken = Cookies.get("next-auth.session-token") || Cookies.get("__Secure-next-auth.session-token")
  let headers = {}
  if (nextToken) {
    headers = {
      "Authorization": `Bearer ${nextToken}`
    }
  }
  const env = createPubEnv()
  console.log(env)
  const data = await fetch(`${publicEnv.API_HOST}/notion/render`, {
    method: "POST",
    body: JSON.stringify(configuration),
    headers: headers
  })
  const json = (await data.json()) as WrappedResponse<RenderResult, Error>
  return json?.data

}


export type SendEmailProps = {
    email: string
    pageUrl: string
    content: string
    captcha: string | undefined | null
}

export const sendTestEmail = async ({email, pageUrl, content, captcha}: SendEmailProps): Promise<any> => {
  const data = await fetch("/api/send-email", {
    method: "POST",
    body: JSON.stringify({
      pageUrl: pageUrl,
      content: content,
      email: email,
      captcha: captcha
    })
  })
  return data
}

export const disconnectNotionAPI = (): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/notion", {method: "DELETE"})
    .then(r => r.json())
}

export const disconnectGmailAPI = (): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/gmail", {method: "DELETE"})
    .then(r => r.json())
}

export const connectSESEmailAPI = (email: string): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/ses", {method: "POST", body: JSON.stringify({email: email})})
    .then(r => r.json())
}

export const connectSESDomainAPI = ({domain, sync}: {domain: string, sync?: boolean}): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/domain", {method: "POST", body: JSON.stringify({domain: domain, sync: sync})})
    .then(r => r.json())
}
export const createNotionDatabase = (data: CreateNotionDatabaseInput): Promise<ApiResponse<{id: string}>> => {
  return fetch("/api/notion/database", {method: "POST", body: JSON.stringify(data)})
    .then(async response => {
      if (!response.ok) {
        const errorBody = await response.json();
        const errorMessage = errorBody.error || 'An unknown error occurred';
        throw new Error(`Error ${response.status}: ${errorMessage}`);
      }
      return response.json();
    });
}

export const createNotionPage = (data: CreateNotionPageInput): Promise<ApiResponse<NotionPageShortData>> => {
  return fetch("/api/notion/page", {method: "POST", body: JSON.stringify(data)})
    .then(async response => {
      if (!response.ok) {
        const errorBody = await response.json();
        const errorMessage = errorBody.error || 'An unknown error occurred';
        throw new Error(`Error ${response.status}: ${errorMessage}`);
      }
      return response.json();
    });
}

export const cancelSubscriptionApi = (): Promise<ApiResponse<void>> => {
  return fetch("/api/subscription", {method: "DELETE"})
    .then(r => r.json())
}

export const disconnectSESEmailAPI = (): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/ses", {method: "DELETE"}).then(r => r.json())
}


export const disconnectSESDomainAPI = (): Promise<ApiResponse<void>> => {
  return fetch("/api/connection/domain", {method: "DELETE"}).then(r => r.json())
}

export const autoconfigureOutboxAPI = (database: DatabaseInfo): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/autoconfig-outbox", {
    method: "POST",
    body: JSON.stringify({id: database.id})
  }).then(r => r.json())
}

export const autoconfigureOutboxRecipientsAPI = (databaseId: string): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/autoconfig-outbox-recipients", {
    method: "POST",
    body: JSON.stringify({id: databaseId})
  }).then(r => r.json())
}

export const autoconfigureInboxAPI = (database: DatabaseInfo): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/autoconfig-inbox", {
    method: "POST",
    body: JSON.stringify({id: database.id})
  }).then(r => r.json())
}


export const sendPageAPI = (database: DatabaseInfo, page: PageData): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/send-page", {
    method: "POST",
    body: JSON.stringify({
      databaseId: database.id,
      pageId: page.id
    })
  }).then(async response => {
    if (!response.ok) {
      const errorBody = await response.json();
      const errorMessage = errorBody.error || 'An unknown error occurred';
      throw new Error(`Error ${response.status}: ${errorMessage}`);
    }
    return response.json();
  });

}

export const setupInboxForDatabaseAPI = (database: DatabaseInfo): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/inbox-config", {
    method: "POST",
    body: JSON.stringify({id: database.id})
  }).then(r => r.json())
}



export const removeInboxForDatabaseAPI = (database: DatabaseInfo): Promise<ApiResponse<void>> => {
  return fetch("/api/notion/inbox-config-delete", {
    method: "POST",
    body: JSON.stringify({id: database.id})
  }).then(r => r.json())
}


export const updateDatabaseConfigAPI = (
  databaseId: string,
  update: {
      inbox: InboxDbConfig | undefined,
      outbox: OutboxDbConfig | undefined
    }
): Promise<ApiResponse<Profile | null | undefined>> => {
  return fetch("/api/db-configuration", {
    method: "POST",
    body: JSON.stringify({ id: databaseId, ...update }),
  })
    .then(async response => {
      if (!response.ok) {
        const errorBody = await response.json();
        const errorMessage = errorBody.error || 'An unknown error occurred';
        throw new Error(`Error ${response.status}: ${errorMessage}`);
      }
      return response.json();
    });
};


export const createNewNotionProperties = (database: DatabaseInfo, properties: any): Promise<ApiResponse<DatabaseInfo>> => {
  return fetch("/api/notion/properties", {
    method: "POST",
    body: JSON.stringify({id: database.id, properties: properties})
  }).then(r => r.json())
}
